import { makeStyles } from '@material-ui/core'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Warning } from '@material-ui/icons'
import CompensationAccordion from '../../../../components/Accordion'
import BonusPayoutContent from './TotalBonusPayout/BonusPayoutContent'
import PayoutSummary from './components/PayoutSummary'
import PayoutLabel from './components/PayoutLabel'
import { usePayoutPopupContext } from './context'
import { compensationPopupColors, useCompensationPopupStyles } from './styles'
import AccordionContent from '../../../../components/Accordion/AccordionContent'
import BonusPayoutSummary from './TotalBonusPayout/BonusPayoutSummary'
import AnnualPayoutSummary from './AnnualPayout/AnnualPayoutSummary'
import AnnualPayoutContent from './AnnualPayout/AnnualPayoutContent'
import useAccordionLabel from './components/AccordionLabel'
import WarningMessage from './components/WarningMessage'
import clsx from 'clsx'

const Content = () => {
  const classes = useStyles()
  const commonClasses = useCompensationPopupStyles()
  const { t } = useTranslation()
  const { data, updatePopoverPosition, isTotalBonusBiggerThanGuaranteed } = usePayoutPopupContext()

  const getClassNames = (isBigger: boolean, isOpened: boolean) => {
    let container: string | undefined = undefined

    if (isBigger) {
      container = isOpened ? commonClasses.activeAqua100Label : commonClasses.whiteLabelWithAquaHover
    } else {
      container = isOpened ? commonClasses.activeGrey300Label : commonClasses.whiteLabelWithGreyHover
    }

    return {
      label: {
        value: clsx(commonClasses.withRightSpace, commonClasses.firstLevelLabel),
        label: commonClasses.firstLevelLabel,
      },
      opened: isBigger ? commonClasses.activeAqua100Label : commonClasses.activeGrey300Label,
      container,
    }
  }

  const getIcon = (isBigger: boolean) => (isBigger ? undefined : () => <Warning className={classes.warningIcon} />)

  const renderTotalBonusPayoutLabel = useAccordionLabel({
    value: data?.totalBonusPayout ?? 0,
    label: 'total_payout_bonus',
    classNames: (isOpened) => getClassNames(isTotalBonusBiggerThanGuaranteed, isOpened),
    icon: getIcon(isTotalBonusBiggerThanGuaranteed),
  })

  const renderAnnualBonusPayoutLabel = useAccordionLabel({
    value: data?.annualPayout?.teamBonus?.annualTeamBonusPayout ?? 0,
    label: 'annual_payout_bonus',
    classNames: (isOpened) => ({
      label: { value: commonClasses.withRightSpace, label: commonClasses.firstLevelLabel },
      opened: commonClasses.activeAqua100Label,
      container: isOpened ? commonClasses.activeAqua100Label : commonClasses.whiteLabelWithAquaHover,
    }),
  })

  const renderGuaranteedPayoutLabel = useAccordionLabel({
    value: data?.guaranteedPayout ?? 0,
    label: 'guaranteed_payout',
    classNames: (isOpened) => getClassNames(!isTotalBonusBiggerThanGuaranteed, isOpened),
    icon: getIcon(!isTotalBonusBiggerThanGuaranteed),
  })

  if (!data) {
    return null
  }

  return (
    <div className={classes.contentContainer}>
      <CompensationAccordion
        classes={{
          opened: isTotalBonusBiggerThanGuaranteed
            ? commonClasses.activeAqua50Content
            : commonClasses.activeGrey100Content,
        }}
        renderLabel={renderTotalBonusPayoutLabel}
        onToggleHandler={updatePopoverPosition}
      >
        <AccordionContent className={commonClasses.withRightSpace}>
          {isTotalBonusBiggerThanGuaranteed ? null : (
            <WarningMessage>{t('compensation_popup_total_bonus_payout_warning_message')}</WarningMessage>
          )}
          <BonusPayoutContent />
        </AccordionContent>

        <PayoutSummary label={t('compensation_popup_total_payout_bonus_label')}>
          <BonusPayoutSummary />
        </PayoutSummary>
      </CompensationAccordion>

      {data.guaranteedPayout ? (
        <CompensationAccordion
          classes={{
            opened: isTotalBonusBiggerThanGuaranteed
              ? commonClasses.activeGrey100Content
              : commonClasses.activeAqua50Content,
          }}
          renderLabel={renderGuaranteedPayoutLabel}
          onToggleHandler={updatePopoverPosition}
        >
          <div className={classes.guaranteedPayoutLabel}>{t('compensation_popup_guaranteed_payout_message')}</div>
        </CompensationAccordion>
      ) : null}

      {data.annualPayout?.teamBonus?.teamBonusRate !== 0 && (
        <CompensationAccordion
          classes={{ opened: commonClasses.activeAqua50Content }}
          renderLabel={renderAnnualBonusPayoutLabel}
          onToggleHandler={updatePopoverPosition}
        >
          <AccordionContent className={commonClasses.withRightSpace}>
            <AnnualPayoutContent />
          </AccordionContent>

          <PayoutSummary label={t('compensation_popup_annual_payout_label')}>
            <AnnualPayoutSummary />
          </PayoutSummary>
        </CompensationAccordion>
      )}

      <div className={classes.fixedPayoutContainer}>
        <PayoutLabel
          classes={{ label: commonClasses.firstLevelLabel, value: commonClasses.firstLevelLabel }}
          label={t('compensation_popup_fixed_payout_label')}
          value={data.fixedPayout}
          format="euroInt"
        />
      </div>
    </div>
  )
}

export default Content

const useStyles = makeStyles({
  contentContainer: {
    fontFamily: 'Lato',
    color: '#212121',
  },
  fixedPayoutContainer: {
    padding: 16,
    paddingLeft: 35,
    paddingRight: 24,
  },
  guaranteedPayoutLabel: {
    fontFamily: 'Lato',
    color: '#212121',
    fontWeight: 700,
    fontSize: 15,
    letterSpacing: '0.02em',
    paddingTop: 8,
    paddingLeft: 32,
    paddingBottom: 16,
    paddingRight: 24,
    maxWidth: 400,
    lineHeight: '130%',
  },
  warningIcon: {
    color: compensationPopupColors.orange,
    height: 18,
  },
})
